import { ISaleStage, ISaleStageCreateDto, ISaleStageUpdateDto, SaleStageType } from "@lib";
import { Modal } from "@core/components/alt-ui/modal";
import { Button, ColorBox, Control, Panel, Select, TextBox } from "@core/components/alt-ui/controls";
import { ISelectOption } from "@core/types/common/select-options";
import { getSaleStageTypes } from "../settings-lists-salestages-defaults";

export class SaleStageModal extends Modal<ISaleStage> {
    private tbSequence!: TextBox;
    private tbName!: TextBox;
    private tbDescription!: TextBox;
    private cbType!: Select<ISelectOption<SaleStageType | null>>;
    private clColor!: ColorBox;
    private pnlFooter!: Panel;
    private btnCancel!: Button;
    private btnSave!: Button;

    private stage: ISaleStage | null = null;
    public onCreate: ((dto: ISaleStageCreateDto) => Promise<ISaleStage | null>) | null = null;
    public onUpdate: ((stage: ISaleStage, dto: ISaleStageUpdateDto) => Promise<ISaleStage | null>) | null = null;

    public constructor() {
        super("sale-stage-modal", "");
        this.initializeControls();
    }

    public show(stage?: ISaleStage): Promise<void> {
        this.stage = stage ?? null;
        this.title = stage ? "Изменение этапа" : "Новый этап";
        this.initializeControls();

        if (stage) {
            this.populateControls(stage);
        }

        return super.show();
    }

    protected initializeControls(): void {
        this.tbSequence = new TextBox();
        this.tbSequence.id = "stage.sequence";
        this.tbSequence.label = "Порядок";
        this.tbSequence.validation = "required|numeric|min_value:1|max_value:999";
        this.tbSequence.visible = false;

        this.tbName = new TextBox();
        this.tbName.id = "stage.name";
        this.tbName.label = "Название";
        this.tbName.validation = "required";

        this.tbDescription = new TextBox();
        this.tbDescription.id = "stage.description";
        this.tbDescription.label = "Описание";

        this.cbType = new Select<ISelectOption<SaleStageType | null>>();
        this.cbType.id = "stage.type";
        this.cbType.label = "Тип";
        this.cbType.items = getSaleStageTypes();
        this.cbType.textField = opt => opt.name;
        this.cbType.selectedIndex = 0;

        this.clColor = new ColorBox();
        this.clColor.id = "stage.color";
        this.clColor.label = "Цвет";
        this.clColor.value = "#98afff";

        //

        this.btnCancel = new Button();
        this.btnCancel.id = "stage.cancel";
        this.btnCancel.text = "Отменить";
        this.btnCancel.class = "mr-0.75";
        this.btnCancel.variant = "outline-danger";
        this.btnCancel.addClickHandler(this.clickCancel.bind(this));

        this.btnSave = new Button();
        this.btnSave.id = "stage.save";
        this.btnSave.text = "Сохранить";
        this.btnSave.addClickHandler(this.clickSave.bind(this));

        this.pnlFooter = new Panel();
        this.pnlFooter.class = "flex justify-end mt-2";
        this.pnlFooter.addControl(this.btnCancel);
        this.pnlFooter.addControl(this.btnSave);
    }

    private populateControls(stage: ISaleStage): void {
        this.tbSequence.text = stage.sequence.toString();
        this.tbSequence.visible = true;
        this.tbName.text = stage.name;
        this.tbDescription.text = stage.description ?? "";
        this.clColor.value = stage.color ?? "#98afff";

        const typeIndex = this.cbType.items.findIndex(i => i.id === stage.type);
        this.cbType.selectedIndex = typeIndex >= 0 ? typeIndex : 0;
    }

    public get controls(): Control[] {
        return [this.tbSequence, this.tbName, this.tbDescription, this.cbType, this.clColor, this.pnlFooter];
    }

    private async clickCancel(sender: any, e: any): Promise<void> {
        this.hide();
    }

    private async clickSave(sender: any, e: any): Promise<void> {
        const valid = await this.validate();
        if (!valid) {
            return;
        }

        const result = this.stage ? await this.updateStage(this.stage) : await this.createStage();

        if (result) {
            this.hide(result);
        }
    }

    private async createStage(): Promise<ISaleStage | null> {
        if (!this.onCreate) {
            return null;
        }

        const dto: ISaleStageCreateDto = {
            type: this.cbType.selectedItem?.id ?? undefined,
            name: this.tbName.text,
            description: this.tbDescription.text.length > 0 ? this.tbDescription.text : undefined,
            color: this.clColor.value,
        };

        return await this.onCreate(dto);
    }

    private async updateStage(stage: ISaleStage): Promise<ISaleStage | null> {
        if (!this.onUpdate) {
            return null;
        }

        const dto: ISaleStageUpdateDto = {
            sequence: parseInt(this.tbSequence.text),
            type: this.cbType.selectedItem?.id ?? undefined,
            name: this.tbName.text,
            description: this.tbDescription.text.length > 0 ? this.tbDescription.text : undefined,
            color: this.clColor.value,
        };

        return await this.onUpdate(stage, dto);
    }
}
